import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";

const DEFAULT_TIERS = [
  {
    key: "open",
    name: "Open Temple",
    price: "Free",
    tag: "Come and receive.",
    cta: "Create free account",
    features: [
      "Public podcast, teachings & readings",
      "Temple Radio live stream",
      "Public Temple gatherings",
      "Save teachings & manage your profile",
      "Course 1 · Encounter 1 — the free introduction",
    ],
  },
  {
    key: "learning",
    name: "Learning Circle",
    price: "$59 / month",
    tag: "Enter the work.",
    highlight: true,
    cta: "Join the Learning Circle",
    features: [
      "Full Learning Circle curriculum — every Encounter",
      "My Journey progress tracking",
      "Private Journal & Reflections",
      "Practices and integration work",
      "Learning Companion (grounded study guide)",
      "Deeper source library & downloads",
      "Member workshops & Learning Circles",
      "Cancel anytime · access continues to period end",
    ],
  },
  {
    key: "temple",
    name: "Temple Circle",
    price: "$119 / month",
    tag: "Come closer to the conversation.",
    cta: "Join Temple Circle",
    features: [
      "Everything in Learning Circle",
      "Monthly small-group Temple Circle with Yisra'el",
      "Live Q&A / dialogue in defined group settings",
      "Priority submission of questions & topics",
      "Temple roundtables & deeper community conversations",
      "Early access to new programs",
      "Priority retreat registration",
      "Special Temple Circle retreat experiences",
    ],
  },
];

export default function Join() {
  const { user, membership } = useApp();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [busy, setBusy] = useState(false);
  const hasAccess = !!membership?.has_access;

  useEffect(() => {
    if (sp.get("cancelled") === "1") toast("Checkout cancelled. You have not been charged.");
  }, [sp]);

  useEffect(() => {
    api.get("/content/settings").then(({ data }) => {
      const s = data || {};
      setTiers(DEFAULT_TIERS.map((t) => ({
        ...t,
        name: s[`tier_${t.key}_name`] || t.name,
        price: t.key === "learning" ? t.price : (s[`tier_${t.key}_price`] || t.price),
        tag: s[`tier_${t.key}_tag`] || t.tag,
      })));
    }).catch(() => {});
  }, []);

  const joinLearningCircle = async () => {
    if (!user) { navigate("/login?next=/join"); return; }
    if (hasAccess) { navigate("/learn"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/membership/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start checkout right now.");
      setBusy(false);
    }
  };

  const onChoose = (t) => {
    if (t.key === "open") { navigate(user ? "/my-temple" : "/login"); return; }
    if (t.key === "learning") { joinLearningCircle(); return; }
    toast("Temple Circle is coming soon. We'll announce when it opens.");
  };

  const ctaLabel = (t) => {
    if (t.key === "open") return user ? "Open My Temple" : t.cta;
    if (t.key === "learning") {
      if (busy) return "Opening secure checkout…";
      if (hasAccess) return membership?.is_admin ? "You have full access" : "You're a member · Continue learning";
      return user ? `${t.cta} · $59/mo` : "Sign in to join";
    }
    return `${t.cta} · Coming soon`;
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-center max-w-2xl mx-auto rise">
          <div className="eyebrow mb-3">Join the Temple</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "var(--burgundy)" }}>
            An invitation, not a transaction.
          </h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--text-obsidian)" }}>
            You don't pay to belong. You join the Learning Circle when you're ready to do the deeper work.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 grid md:grid-cols-3 gap-6 pt-3">
        {tiers.map((t) => (
          <div key={t.key} data-testid={`membership-tier-${t.key}`}
            className={`rounded-3xl p-8 border relative flex flex-col ${t.highlight ? "lg:-translate-y-3" : ""}`}
            style={{
              background: t.highlight ? "var(--burgundy)" : "var(--bg-cream-card)",
              color: t.highlight ? "var(--bg-cream)" : "var(--text-obsidian)",
              borderColor: t.highlight ? "var(--gold)" : "var(--border-cream)",
              borderWidth: t.highlight ? "2px" : "1px",
            }}>
            {t.highlight && (
              <div data-testid="membership-badge-most-walked" className="inline-flex self-start rounded-full px-3 py-1 mb-4 text-[10px] font-mono uppercase tracking-widest" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                Most walked path
              </div>
            )}
            <div className="eyebrow mb-2" style={{ color: t.highlight ? "var(--gold-soft)" : "var(--gold-dark)" }}>{t.tag}</div>
            <div className="font-serif text-3xl">{t.name}</div>
            <div className="font-serif text-2xl mt-1 opacity-90" data-testid={`membership-price-${t.key}`}>{t.price}</div>
            <ul className="mt-6 space-y-3">
              {t.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.highlight ? "var(--gold)" : "var(--burgundy)" }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button data-testid={`membership-choose-${t.key}-btn`} onClick={() => onChoose(t)} disabled={busy && t.key === "learning"}
              className="mt-auto pt-0 w-full rounded-full py-3 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
              style={{ marginTop: "2rem", background: t.highlight ? "var(--gold)" : "var(--burgundy)", color: t.highlight ? "var(--burgundy)" : "var(--bg-cream)" }}>
              {busy && t.key === "learning" && <Loader2 className="w-4 h-4 animate-spin" />}{ctaLabel(t)}
            </button>
            {t.key === "learning" && !hasAccess && (
              <p className="text-[11px] text-center mt-2 opacity-80" data-testid="membership-learning-note">Secure checkout by Stripe · cancel anytime from My Temple.</p>
            )}
            {t.key === "temple" && (
              <p className="text-[11px] text-center mt-2 opacity-70" data-testid="membership-temple-note">Not yet open for enrollment.</p>
            )}
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14" data-testid="join-support-section">
        <div className="rounded-3xl p-8 sm:p-10 grid md:grid-cols-3 gap-6 items-center" style={{ background: "var(--surface-deep)", color: "var(--bg-cream)" }}>
          <div className="md:col-span-2">
            <div className="eyebrow mb-2" style={{ color: "var(--gold-soft)" }}>Support the Temple</div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-snug">If this work has supported your journey, you are welcome to give.</h2>
            <p className="mt-3 text-sm opacity-90 max-w-xl">One-time gifts and monthly support help sustain the teachings, gatherings, Temple Radio, and writings. Giving is optional and never a condition of belonging.</p>
          </div>
          <div className="md:text-right">
            <Link to="/give" data-testid="join-give-btn" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium" style={{ background: "var(--gold)", color: "var(--burgundy)" }}>Give <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <section className="border-t" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 text-center">
          <Sparkles className="w-6 h-6 mx-auto mb-4" style={{ color: "var(--gold-dark)" }} />
          <div className="eyebrow mb-3">The Covenant</div>
          <p className="font-serif text-2xl sm:text-3xl leading-snug" style={{ color: "var(--burgundy)" }}>
            "I will practice presence, in small and ordinary ways.
            I will let compassion be trained in me as a discipline.
            I will trust that the Divine has always been within, and will let that discovery show in how I live."
          </p>
          <p className="mt-6 text-sm" style={{ color: "var(--text-muted)" }}>
            No creed. No condition. Only a willingness to keep returning.
          </p>
          <p className="mt-4 text-xs italic" style={{ color: "var(--text-muted)" }}>
            Higher tiers offer more time with the community; they do not confer greater spiritual status or greater access to the Divine.
          </p>
        </div>
      </section>
    </div>
  );
}
